import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { post } from "@common/api/apiShared";
import { useDotLegalMutation, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { useUrlProvider } from "../../../useUrlProvider";
import { AssigneeType } from "../../SelectableAssignee.types";
import { ISendAssessmentsDialogProps } from "./SendAssessmentsDialog";

export default function useSendAssessmentsDialog(props: ISendAssessmentsDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const navigate = useNavigate();
    const queryClient = useDotLegalQueryClient();
    const { translateString } = useTranslation();
    const { getInternalAssessmentGroupUrl } = useUrlProvider();

    const [isSending, setIsSending] = useState(false);
    const [sentCount, setSentCount] = useState(0);
    const [failed, setFailed] = useState(false);

    const numberOfRecipients = props.selectedAssignees.length;

    const sendMutation = useDotLegalMutation((assignee: { assigneeId: string; assigneeType: AssigneeType }) =>
        post<{}>(`/internalAssessmentGroups/${props.internalAssessmentGroupId}/sendAssessment`, assignee)
    );

    const send = async () => {
        setIsSending(true);
        setSentCount(0);
        setFailed(false);

        let sent = 0;

        for (const assignee of props.selectedAssignees) {
            try {
                await sendMutation.mutateAsync({ assigneeId: assignee.id, assigneeType: assignee.assigneeType });
            } catch {
                // Each successful send removes its own draft server-side, so refetching leaves exactly the
                // recipients that were not reached selected and the send can simply be retried. Without the
                // refetch a retry would work from the list this dialog opened with and send twice to everyone
                // the first attempt did reach.
                await queryClient.refetchQueries(`/internalAssessmentGroups/${props.internalAssessmentGroupId}`);
                setFailed(true);
                setIsSending(false);
                snackbar.show(translateString("internalAssessmentGroupSendFailed"), "warning");
                return;
            }

            sent += 1;
            setSentCount(sent);
        }

        setIsSending(false);
        snackbar.show(createElement(Trans, { i18nKey: "internalAssessmentGroupSent", values: { name: props.internalAssessmentGroupName } }));
        navigate(getInternalAssessmentGroupUrl(props.internalAssessmentGroupId));
    };

    return {
        send,
        isSending,
        failed,
        sentCount,
        numberOfRecipients,
        progress: numberOfRecipients === 0 ? 0 : (sentCount / numberOfRecipients) * 100,
    };
}
